import { VesperPoolReadAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Vesper v5 pool (`va*`) — three calls:
 *   1. `totalValue()`     — buffer + Σ strategy holdings, raw underlying
 *   2. `totalSupply()`
 *   3. `pricePerShare()`  — raw UNDERLYING units per one whole share
 *
 * Not ERC-4626 in any part: `asset()`, `totalAssets()`, `convertToAssets()`
 * and every `max*` view revert. The pool's own surface is `token()` /
 * `pricePerShare()` / `totalValue()`, hence a reader.
 *
 * **The pricePerShare scale is the UNDERLYING's, not the share's.** Shares
 * are 18-decimal on every v5 pool, but `pricePerShare()` answers in raw
 * underlying units per whole share — vaUSDC reads `1298315` (1.298315
 * USDC), vamsETH reads `1043306889192393389`. So the 1e18-normalised
 * exchange rate is `pps × 1e18 / underlyingUnit`, and using the share
 * decimals here would price the 6-decimal rows at 1e-12 of par.
 *
 * A missing or zero `pricePerShare` drops the entry rather than deriving a
 * substitute from `totalValue/totalSupply` — the legacy v2 pools (vUSDC /
 * vETH / vWBTC) answer the other two calls but not this one, and failing
 * loudly is what keeps a v2 address out of the registry by accident (they
 * are excluded on purpose: near-zero rates, run-off generation).
 *
 * `totalValue()` is a live computed view over the pool buffer plus every
 * strategy's holdings, so it needs no accrual correction. The rows are
 * registered `instant`: `withdraw(shares)` settles in the same transaction,
 * paying from the buffer and unwinding strategies for the remainder.
 */
export const readerVesperPool = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address: entry.address, name: 'totalValue', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'pricePerShare', params: [] },
    ],
    abis: [VesperPoolReadAbi, VesperPoolReadAbi, VesperPoolReadAbi],
    parse: ([value, supply, pps]) => {
      const totalAssets = toBigInt(value)
      const totalSupply = toBigInt(supply)
      const pricePerShare = toBigInt(pps)
      if (totalAssets === undefined || totalSupply === undefined) {
        return undefined
      }
      // No pricePerShare ⇒ not a v5 pool (or a broken read) — drop the
      // entry rather than publish a price on a different basis.
      if (pricePerShare === undefined || pricePerShare <= 0n) return undefined

      const exchangeRate = (pricePerShare * ONE_E18) / underlyingUnit

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
